import React from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, IndianRupee, Home as HomeIcon, Users, BedDouble, CalendarDays, Store, Factory, Utensils, Warehouse, Trees } from "lucide-react";
import { inr } from "@/lib/api";
import { CATEGORY_MAP, categoryHeadline } from "@/lib/categories";
import VerifiedBadge from "@/components/locanest/VerifiedBadge";
import TierBadge from "@/components/locanest/TierBadge";
import ShareButton from "@/components/locanest/ShareButton";
import { Star } from "lucide-react";

const ICONS = { home: HomeIcon, pg: Users, room: BedDouble, short_stay: CalendarDays, shop: Store, factory: Factory, cafe: Utensils, warehouse: Warehouse, land: Trees };

function PropertyCard({ p, isFav, onFavToggle }) {
  const cat = CATEGORY_MAP[p.category] || { label: p.category };
  const Icon = ICONS[p.category] || HomeIcon;

  return (
    <Link
      to={`/property/${p.id}`}
      className="group block bg-[#F0EBE1] rounded-lg overflow-hidden border border-[#1A1F2C]/10 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
      data-testid={`property-card-${p.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#1A1F2C]/5">
        <img
          src={p.photos?.[0] || "https://images.pexels.com/photos/7587828/pexels-photo-7587828.jpeg?auto=compress&cs=tinysrgb&w=800"}
          alt={p.title}
          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${p.is_available === false ? "grayscale" : ""}`}
          loading="lazy"
        />
        {p.is_available === false && (
          <div className="absolute inset-0 bg-[#1A1F2C]/45 flex items-center justify-center" data-testid={`out-of-stock-${p.id}`}>
            <div className="px-4 py-2 bg-[#FDFBF7] rounded shadow-lg text-xs uppercase tracking-[0.2em] font-semibold text-[#C85A32]">
              Out of stock
            </div>
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-col items-start gap-1">
          <div className="px-2.5 py-1 bg-[#FDFBF7]/90 backdrop-blur text-[10px] uppercase tracking-[0.18em] rounded inline-flex items-center gap-1">
            <Icon size={11} /> {cat.label}
          </div>
          {p.owner_tier && p.owner_tier !== "new" && <TierBadge tier={p.owner_tier} />}
        </div>
        {onFavToggle && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFavToggle(p.id); }}
            className={`absolute top-3 right-3 p-2 rounded-full backdrop-blur ${isFav ? "bg-[#C85A32] text-white" : "bg-[#FDFBF7]/90 text-[#1A1F2C]"}`}
            aria-label="favorite"
            data-testid={`fav-toggle-${p.id}`}
          >
            <Heart size={16} fill={isFav ? "currentColor" : "none"} />
          </button>
        )}
        <ShareButton property={p} variant="icon" testId={`card-share-${p.id}`} />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-1">
          {p.owner_verified && <VerifiedBadge />}
          {p.owner_rating_count > 0 && (
            <span className="inline-flex items-center gap-0.5 text-xs text-[#5C6B7A]" data-testid={`rating-inline-${p.id}`}>
              <Star size={11} fill="#C85A32" color="#C85A32" /> <strong className="text-[#1A1F2C]">{p.owner_rating_avg.toFixed(1)}</strong> ({p.owner_rating_count})
            </span>
          )}
        </div>
        <h3 className="font-display text-lg text-[#1A1F2C] leading-tight truncate" data-testid={`property-title-${p.id}`}>{p.title}</h3>
        <div className="mt-1 flex items-center gap-1 text-sm text-[#5C6B7A]">
          <MapPin size={13} /> <span className="truncate">{p.locality}, {p.city}</span>
        </div>

        <div className="mt-3 text-sm text-[#1A1F2C]/80 truncate">
          {categoryHeadline(p.category, p.category_fields)}
        </div>

        <div className="mt-4 pt-4 border-t border-[#1A1F2C]/10 flex items-center justify-between">
          <div className="flex items-center text-[#C85A32] font-semibold">
            <IndianRupee size={15} />{(p.category === "short_stay" ? (p.category_fields?.price_per_night || 0) : (p.rent || 0)).toLocaleString("en-IN")}
            <span className="text-xs text-[#5C6B7A] font-normal ml-1">{p.category === "short_stay" ? "/night" : "/mo"}</span>
          </div>
          {p.deposit ? <span className="text-xs text-[#5C6B7A]">Deposit {inr(p.deposit)}</span> : null}
        </div>
      </div>
    </Link>
  );
}

// Memoize the card — listings pages render dozens; parent re-renders (favorites
// toggling, filters etc.) no longer need to reconcile the entire grid.
export default React.memo(PropertyCard, (prev, next) =>
  prev.p?.id === next.p?.id &&
  prev.p?.is_available === next.p?.is_available &&
  prev.p?.owner_tier === next.p?.owner_tier &&
  prev.p?.owner_verified === next.p?.owner_verified &&
  prev.p?.owner_rating_avg === next.p?.owner_rating_avg &&
  prev.p?.owner_rating_count === next.p?.owner_rating_count &&
  prev.p?.contact_gated === next.p?.contact_gated &&
  prev.p?.title === next.p?.title &&
  prev.p?.locality === next.p?.locality &&
  prev.p?.rent === next.p?.rent &&
  prev.p?.photos?.[0] === next.p?.photos?.[0] &&
  prev.isFav === next.isFav &&
  prev.onFavToggle === next.onFavToggle
);
